import { Injectable, EventEmitter, ElementRef } from '@angular/core';
import { KeyboardNavigationKeys, KeyboardKey } from '@nha/enums';
import { gridActionclearActiveCells$ } from './actions/actions';
import { areEqual } from '@nha/functions/are-equal.function';

@Injectable()
export class GridService {
	private _gridRef: ElementRef;
	private _grid: HTMLElement;
	private _properties: { activeRow: number; activeCell: number } = {
		activeCell: -1,
		activeRow: -1
	};
	private _scrollIntoViewTimeout: number = null;
	private _hasError: boolean;
	private _cellActivated: EventEmitter<HTMLElement> = new EventEmitter<HTMLElement>();

	initialize(gridRef: ElementRef): void {
		this._gridRef = gridRef;
		this._grid = this._gridRef.nativeElement;
	}

	configureScrolling(): void {
		const gridHeader: HTMLElement = this.getGridHeader();
		const gridData: HTMLElement = this.getGridData();

		gridData.onscroll = () => {
			gridHeader.scrollLeft = gridData.scrollLeft;
		};
	}

	configureKeyboardNavigation(): void {
		window.setTimeout(() => {
			const rows = this.getAllRows();
			const dataRows = [];

			if (rows.length > 0) {
				rows.forEach((pdr: HTMLElement, i: number) => {
					const dataRowCells = [].concat(Array.from(pdr.querySelectorAll('.grid-cell')), Array.from(rows[i].querySelectorAll('.grid-cell')));
					dataRows.push(dataRowCells);
				});
			}

			dataRows.forEach((cells: HTMLElement[], i: number) => {
				cells.forEach((cell: HTMLElement, j: number) => {
					cell.setAttribute('tabindex', `0`);
					cell.setAttribute('row-index', `${i}`);
					cell.setAttribute('cell-index', `${j}`);
					cell.setAttribute('row-count', `${dataRows.length}`);
					cell.setAttribute('cell-count', `${cells.length}`);

					cell.onclick = () => this.cellClick(cell);
					cell.onkeyup = (event: KeyboardEvent) => this.cellKeyup(event, cell);
					cell.onkeydown = (event: KeyboardEvent) => this.cellKeydown(event);
				});
			});
		}, 10);
	}

	scrollIntoView(cell: HTMLElement = null): void {
		const gridDataArea: HTMLElement = this._grid.querySelector('.grid-data');
		const gridDataUnpinned: HTMLElement = gridDataArea.querySelector('.grid-columns');

		if (cell === null) {
			gridDataArea.scrollTo(0, 0);
			gridDataUnpinned.scrollTo(0, 0);
			return;
		}

		this.scrollLeftRight(cell, gridDataUnpinned);
		this.scrollUpDown(cell, gridDataArea);
	}

	setHasError(hasError: boolean): void {
		this._hasError = hasError;
	}

	getCellActivated(): EventEmitter<HTMLElement> {
		return this._cellActivated;
	}

	removeActiveCellState(): void {
		const allCells: HTMLElement[] = Array.from(this._grid.querySelectorAll('.grid-data .grid-cell.active'));
		allCells.forEach((c) => {
			c.classList.remove('active');
		});

		this.removeActiveRowState();
	}

	private getGridHeader(): HTMLElement {
		return this._grid?.querySelector('.grid-headers');
	}

	private getGridData(): HTMLElement {
		const dataGridArea: HTMLElement = this._grid?.querySelector('.grid-data');
		const virtualScrollArea: HTMLElement = dataGridArea.querySelector('.scroll-container');

		if (virtualScrollArea) {
			return virtualScrollArea;
		}

		return dataGridArea;
	}

	private getAllRows(): HTMLElement[] {
		return Array.from(this._grid.querySelectorAll('.grid-data .grid-data-row'));
	}

	private removeActiveRowState(rows: HTMLElement[] = null): void {
		if (rows === null) {
			rows = this.getAllRows();
		}

		rows.forEach((row) => row.classList.remove('active'));
	}

	private setActiveRow(index: number): void {
		const rows = this.getAllRows();
		this.removeActiveRowState(rows);

		rows.forEach((row, i) => (index === i ? row.classList.add('active') : null));
	}

	private scrollLeftRight(cell: HTMLElement, gridDataUnpinned: HTMLElement): void {
		const cellCoords = cell.getBoundingClientRect();
		const unPinnedCoords = gridDataUnpinned.getBoundingClientRect();

		if (cellCoords.left < unPinnedCoords.left) {
			const diff = (unPinnedCoords.left - cellCoords.left) * -1;
			gridDataUnpinned.scrollBy(diff, 0);
		}
		if (cellCoords.right > unPinnedCoords.right) {
			const diff = cellCoords.right - unPinnedCoords.right;
			gridDataUnpinned.scrollBy(diff, 0);
		}
	}

	private scrollUpDown(cell: HTMLElement, gridDataArea: HTMLElement): void {
		if (this._scrollIntoViewTimeout !== null) {
			window.clearTimeout(this._scrollIntoViewTimeout);
		}
		this._scrollIntoViewTimeout = window.setTimeout(() => {
			const gridDataCoords2 = gridDataArea.getBoundingClientRect();
			const cellCoords2 = cell.getBoundingClientRect();

			if (cellCoords2.top < gridDataCoords2.top) {
				const diff = (gridDataCoords2.top - cellCoords2.top + 1) * -1;
				gridDataArea.scrollBy(0, diff);
			}

			if (cellCoords2.bottom + 18 > gridDataCoords2.bottom) {
				const diff = cellCoords2.bottom - gridDataCoords2.bottom + 18;
				gridDataArea.scrollBy(0, diff);
			}

			this._scrollIntoViewTimeout = null;
		}, 10);
	}

	private cellClick(cell: HTMLElement): void {
		if (this._hasError) {
			return;
		}

		this.removeActiveCellState();

		this._properties.activeRow = parseInt(cell.getAttribute('row-index'));
		this._properties.activeCell = parseInt(cell.getAttribute('cell-index'));

		this.setActiveCell(cell);
	}

	private cellKeyup(event: KeyboardEvent, cell: HTMLElement): void {
		if (!KeyboardNavigationKeys.includes(event.key as KeyboardKey) || this._hasError) {
			return;
		}

		this.removeActiveCellState();

		const currentProperties: { activeRow: number; activeCell: number } = {
			activeCell: this._properties.activeCell,
			activeRow: this._properties.activeRow
		};

		const rowCount: number = parseInt(cell.getAttribute('row-count'));
		const cellCount: number = parseInt(cell.getAttribute('cell-count'));

		if (event.key === KeyboardKey.ArrowUp && this._properties.activeRow !== 0) {
			this._properties.activeRow--;
		} // up
		if ([KeyboardKey.Enter, KeyboardKey.ArrowDown].includes(event.key as KeyboardKey) && this._properties.activeRow < rowCount - 1) {
			this._properties.activeRow++;
		} // down
		if (event.key === KeyboardKey.ArrorwLeft && this._properties.activeCell !== 0) {
			this._properties.activeCell--;
		} // left
		if ([KeyboardKey.Tab, KeyboardKey.ArrowRight].includes(event.key as KeyboardKey) && this._properties.activeCell < cellCount - 1) {
			this._properties.activeCell++;
		} // right

		if (!areEqual(currentProperties, this._properties)) {
			const newActiveCell: HTMLElement = this._grid.querySelector(
				`.grid-data .grid-cell[row-index="${this._properties.activeRow}"][cell-index="${this._properties.activeCell}"]`
			);

			this.setActiveCell(newActiveCell);
		}
	}

	private cellKeydown(event): void {
		if (KeyboardNavigationKeys.includes(event.key as KeyboardKey)) {
			event.preventDefault();
			event.stopPropagation();
		}
	}

	private setActiveCell(cell: HTMLElement) {
		gridActionclearActiveCells$.next(false);

		this.scrollIntoView(cell);
		this.setActiveRow(this._properties.activeRow);

		cell.classList.add('active');
		cell.focus();

		this._cellActivated.emit(cell);
	}
}
